import React, { useState, useEffect, useRef } from "react";
import axios from "../utils/axiosInstance";
import {
  InputGroup,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import * as Icon from "react-bootstrap-icons";

const API_URL = "/message";
const API_URL1 = "/message_reciever";
const API_URL2 = "/message_akagari";

export default function Message() {
  const [messageText, setMessageText] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [selectedSupId, setSelectedSupId] = useState("");

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ show: false, type: "", msg: "" });
  const [showSearch, setShowSearch] = useState(false);
  const [showRecipientPicker, setShowRecipientPicker] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [showEmojiHint, setShowEmojiHint] = useState(false);

  const messageEndRef = useRef(null);
  const textareaRef = useRef(null);
  const messageListRef = useRef(null);

  // ─── Window size tracking ─────────────────────────────────────
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 576;
  const isTablet = windowWidth >= 576 && windowWidth < 992;

  // ─── Auto-scroll to bottom ────────────────────────────────────
  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // ─── Auto-resize textarea ────────────────────────────────────
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const scrollH = textareaRef.current.scrollHeight;
      textareaRef.current.style.height =
        Math.min(scrollH, isMobile ? 100 : 140) + "px";
    }
  }, [messageText, isMobile]);

  // ─── Filtered recipients ─────────────────────────────────────
  const filteredRecipients = recipients.filter((p) => {
    const name = (p.fullname || `Umuyobozi ${p.user_id}`).toLowerCase();
    return name.includes(recipientSearch.toLowerCase());
  });

  const selectedRecipientName = (() => {
    const found = recipients.find(
      (p) => String(p.user_id) === String(selectedSupId)
    );
    if (found) return found.fullname || `Umuyobozi ${found.user_id}`;
    return "";
  })();

  // ─── Modern CSS ──────────────────────────────────────────────
  useEffect(() => {
    const id = "whatsapp-modern-2025";
    if (document.getElementById(id)) return;

    const css = `
      @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800&display=swap');

      *, *::before, *::after {
        box-sizing: border-box;
        -webkit-tap-highlight-color: transparent;
      }

      html {
        -webkit-text-size-adjust: 100%;
        scroll-behavior: smooth;
      }

      /* ── Page Background ── */
      .message-page {
        background: #0b141a;
        min-height: 100vh;
        min-height: 100dvh;
        font-family: 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        color: #e9edef;
        overflow: hidden;
        position: relative;
      }

      .message-page::before {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background:
          radial-gradient(ellipse at 20% 50%, rgba(0,168,132,0.03) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 20%, rgba(0,168,132,0.02) 0%, transparent 50%);
        pointer-events: none;
        z-index: 0;
      }

      /* ── Chat Pattern Overlay ── */
      .chat-pattern {
        position: fixed;
        top: 0; left: 0; right: 0; bottom: 0;
        opacity: 0.015;
        background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300a884' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        pointer-events: none;
        z-index: 0;
      }

      /* ── Animations ── */
      @keyframes slideInUp {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }

      @keyframes slideInDown {
        from { opacity: 0; transform: translateY(-20px); }
        to { opacity: 1; transform: translateY(0); }
      }

      @keyframes fadeInScale {
        from { opacity: 0; transform: scale(0.92); }
        to { opacity: 1; transform: scale(1); }
      }

      @keyframes bubbleIn {
        0% { opacity: 0; transform: scale(0.8) translateY(12px); }
        60% { transform: scale(1.02) translateY(-2px); }
        100% { opacity: 1; transform: scale(1) translateY(0); }
      }

      @keyframes toastSlide {
        from { opacity: 0; transform: translateX(100px); }
        to { opacity: 1; transform: translateX(0); }
      }

      @keyframes sendPulse {
        0% { transform: scale(1); }
        50% { transform: scale(0.92); }
        100% { transform: scale(1); }
      }

      @keyframes typingDot {
        0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
        30% { transform: translateY(-6px); opacity: 1; }
      }

      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }

      @keyframes recipientSlide {
        from { opacity: 0; transform: translateY(100%); }
        to { opacity: 1; transform: translateY(0); }
      }

      @keyframes checkmark {
        0% { stroke-dashoffset: 24; }
        100% { stroke-dashoffset: 0; }
      }

      @keyframes pulse {
        0% { box-shadow: 0 0 0 0 rgba(0,168,132,0.4); }
        70% { box-shadow: 0 0 0 12px rgba(0,168,132,0); }
        100% { box-shadow: 0 0 0 0 rgba(0,168,132,0); }
      }

      /* ── Header ── */
      .wa-header {
        background: linear-gradient(180deg, #1f2c34 0%, #1a252d 100%);
        padding: 0;
        position: sticky;
        top: 0;
        z-index: 100;
        border-bottom: 1px solid rgba(255,255,255,0.04);
        backdrop-filter: blur(20px);
        -webkit-backdrop-filter: blur(20px);
      }

      .wa-header-inner {
        max-width: 1200px;
        margin: 0 auto;
        padding: 12px 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }

      @media (min-width: 768px) {
        .wa-header-inner {
          padding: 14px 24px;
        }
      }

      .wa-avatar {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background: linear-gradient(135deg, #00a884 0%, #008f6f 50%, #025d4b 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-weight: 800;
        font-size: 1.1rem;
        flex-shrink: 0;
        box-shadow: 0 4px 14px rgba(0,168,132,0.3);
        position: relative;
      }

      .wa-avatar::after {
        content: '';
        position: absolute;
        bottom: 1px;
        right: 1px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #00d264;
        border: 2px solid #1f2c34;
        animation: pulse 2s infinite;
      }

      .wa-header-title {
        font-size: 1.05rem;
        font-weight: 700;
        color: #e9edef;
        margin: 0;
        line-height: 1.2;
      }

      .wa-header-sub {
        font-size: 0.72rem;
        color: #8696a0;
        margin: 0;
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .wa-header-sub .online-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #00a884;
        display: inline-block;
        animation: pulse 2s infinite;
      }

      .wa-header-actions {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .wa-header-btn {
        background: transparent;
        border: none;
        color: #8696a0;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        cursor: pointer;
        position: relative;
        font-size: 1.1rem;
      }

      .wa-header-btn:hover {
        background: rgba(255,255,255,0.06);
        color: #e9edef;
      }

      .wa-header-btn:active {
        transform: scale(0.9);
        background: rgba(255,255,255,0.1);
      }

      /* ── Search Bar ── */
      .search-bar {
        background: #1f2c34;
        border-bottom: 1px solid rgba(255,255,255,0.04);
        padding: 10px 16px;
        animation: slideInDown 0.3s ease;
      }

      .search-bar-inner {
        max-width: 1200px;
        margin: 0 auto;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .search-input-wrap {
        flex: 1;
        position: relative;
      }

      .search-input-wrap i {
        position: absolute;
        left: 14px;
        top: 50%;
        transform: translateY(-50%);
        color: #8696a0;
        font-size: 0.88rem;
        pointer-events: none;
      }

      .search-input {
        width: 100%;
        background: #2a3942;
        border: none;
        color: #e9edef;
        padding: 10px 14px 10px 40px;
        border-radius: 10px;
        font-size: 0.92rem;
        font-family: inherit;
        outline: none;
        transition: all 0.2s ease;
      }

      .search-input:focus {
        background: #323f49;
        box-shadow: 0 0 0 2px rgba(0,168,132,0.3);
      }

      .search-input::placeholder {
        color: #8696a0;
      }

      .search-close-btn {
        background: rgba(255,255,255,0.05);
        border: none;
        color: #8696a0;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .search-close-btn:hover {
        background: rgba(255,255,255,0.1);
        color: #e9edef;
      }

      /* ── Chat Container ── */
      .wa-chat-container {
        max-width: 1200px;
        margin: 0 auto;
        height: calc(100vh - 70px);
        height: calc(100dvh - 70px);
        display: flex;
        flex-direction: column;
        position: relative;
        z-index: 1;
      }

      @media (min-width: 768px) {
        .wa-chat-container {
          margin: 8px auto;
          height: calc(100vh - 86px);
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.04);
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
      }

      @media (min-width: 1200px) {
        .wa-chat-container {
          margin: 12px auto;
          height: calc(100vh - 94px);
          max-width: 900px;
        }
      }

      /* ── Message List ── */
      .wa-message-list {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 16px 12px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        
        -webkit-overflow-scrolling: touch;
        scroll-behavior: smooth;
      }

      @media (min-width: 768px) {
        .wa-message-list {
          padding: 20px 40px;
          gap: 6px;
        }
      }

      @media (min-width: 1200px) {
        .wa-message-list {
          padding: 24px 60px;
        }
      }

      /* Scrollbar */
      .wa-message-list::-webkit-scrollbar {
        width: 5px;
      }

      .wa-message-list::-webkit-scrollbar-track {
        background: transparent;
      }

      .wa-message-list::-webkit-scrollbar-thumb {
        background: rgba(255,255,255,0.08);
        border-radius: 10px;
      }

      .wa-message-list::-webkit-scrollbar-thumb:hover {
        background: rgba(0,168,132,0.3);
      }

      /* ── Message Bubbles ── */
      .wa-bubble-wrap {
        display: flex;
        margin-bottom: 2px;
        animation: bubbleIn 0.35s cubic-bezier(0.16, 1, 0.3, 1);
      }

      .wa-bubble-wrap.sent {
        justify-content: flex-end;
      }

      .wa-bubble-wrap.received {
        justify-content: flex-start;
      }

      .wa-bubble {
        max-width: 82%;
        padding: 8px 12px 6px;
        position: relative;
        word-wrap: break-word;
        overflow-wrap: break-word;
        line-height: 1.45;
        font-size: 0.92rem;
      }

      @media (min-width: 768px) {
        .wa-bubble {
          max-width: 65%;
          padding: 10px 14px 8px;
          font-size: 0.94rem;
        }
      }

      @media (min-width: 1200px) {
        .wa-bubble {
          max-width: 55%;
        }
      }

      .wa-bubble.sent {
        background: linear-gradient(135deg, #005c4b 0%, #004e41 100%);
        border-radius: 12px 12px 4px 12px;
        margin-left: 48px;
      }

      .wa-bubble.received {
        background: linear-gradient(135deg, #202c33 0%, #1d2930 100%);
        border-radius: 12px 12px 12px 4px;
        margin-right: 48px;
        border: 1px solid rgba(255,255,255,0.03);
      }

      /* Bubble tail */
      .wa-bubble.sent::before {
        content: '';
        position: absolute;
        right: -6px;
        bottom: 0;
        width: 0;
        height: 0;
        border-left: 8px solid #004e41;
        border-top: 8px solid transparent;
      }

      .wa-bubble.received::before {
        content: '';
        position: absolute;
        left: -6px;
        bottom: 0;
        width: 0;
        height: 0;
        border-right: 8px solid #202c33;
        border-top: 8px solid transparent;
      }

      .wa-bubble-sender {
        font-size: 0.72rem;
        font-weight: 700;
        margin-bottom: 3px;
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .wa-bubble-sender.sent-label {
        color: #00d264;
      }

      .wa-bubble-sender.received-label {
        color: #00a884;
      }

      .wa-sender-avatar {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 0.55rem;
        font-weight: 800;
        color: #fff;
        flex-shrink: 0;
      }

      .wa-sender-avatar.sent-av {
        background: linear-gradient(135deg, #00a884, #025d4b);
      }

      .wa-sender-avatar.received-av {
        background: linear-gradient(135deg, #3b4a54, #2a3942);
      }

      .wa-bubble-text {
        color: #e9edef;
        margin: 0;
        white-space: pre-wrap;
      }

      .wa-bubble-meta {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 4px;
        margin-top: 4px;
      }

      .wa-bubble-time {
        font-size: 0.65rem;
        color: rgba(255,255,255,0.45);
      }

      .wa-check {
        color: #53bdeb;
        font-size: 0.72rem;
      }

      /* ── Date Separator ── */
      .wa-date-sep {
        text-align: center;
        padding: 12px 0;
      }

      .wa-date-sep span {
        background: rgba(255,255,255,0.06);
        color: #8696a0;
        font-size: 0.7rem;
        font-weight: 600;
        padding: 5px 14px;
        border-radius: 8px;
        letter-spacing: 0.3px;
        text-transform: uppercase;
        backdrop-filter: blur(10px);
      }

      /* ── Empty State ── */
      .wa-empty-state {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 40px 20px;
        text-align: center;
      }

      .wa-empty-icon {
        width: 100px;
        height: 100px;
        border-radius: 50%;
        background: rgba(0,168,132,0.06);
        border: 2px solid rgba(0,168,132,0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 24px;
        animation: fadeInScale 0.5s ease;
      }

      @media (min-width: 768px) {
        .wa-empty-icon {
          width: 120px;
          height: 120px;
        }
      }

      .wa-empty-title {
        color: #e9edef;
        font-size: 1.1rem;
        font-weight: 700;
        margin-bottom: 8px;
      }

      .wa-empty-text {
        color: #8696a0;
        font-size: 0.88rem;
        max-width: 300px;
        line-height: 1.5;
      }

      /* ── Compose Area ── */
      .wa-compose {
        background: linear-gradient(180deg, #1a252d 0%, #1f2c34 100%);
        padding: 10px 12px;
        border-top: 1px solid rgba(255,255,255,0.04);
        position: relative;
        z-index: 10;
      }

      @media (min-width: 768px) {
        .wa-compose {
          padding: 12px 20px;
        }
      }

      /* Safe area for iOS */
      @supports (padding: env(safe-area-inset-bottom)) {
        .wa-compose {
          padding-bottom: calc(10px + env(safe-area-inset-bottom));
        }
      }

      .wa-compose-recipient {
        margin-bottom: 10px;
      }

      .wa-recipient-btn {
        width: 100%;
        background: rgba(0,168,132,0.08);
        border: 1px dashed rgba(0,168,132,0.25);
        color: #8696a0;
        padding: 10px 16px;
        border-radius: 12px;
        font-size: 0.85rem;
        font-family: inherit;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 10px;
        transition: all 0.2s ease;
        text-align: left;
      }

      .wa-recipient-btn:hover {
        background: rgba(0,168,132,0.12);
        border-color: rgba(0,168,132,0.4);
      }

      .wa-recipient-btn:active {
        transform: scale(0.98);
      }

      .wa-recipient-btn.selected {
        border-style: solid;
        border-color: rgba(0,168,132,0.4);
        color: #e9edef;
        background: rgba(0,168,132,0.1);
      }

      .wa-recipient-btn .rec-icon {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: linear-gradient(135deg, #00a884, #025d4b);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-weight: 700;
        font-size: 0.75rem;
        flex-shrink: 0;
      }

      .wa-recipient-btn .rec-label {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .wa-recipient-btn .rec-change {
        font-size: 0.7rem;
        color: #00a884;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        flex-shrink: 0;
      }

      .wa-compose-row {
        display: flex;
        align-items: flex-end;
        gap: 8px;
      }

      .wa-compose-input-wrap {
        flex: 1;
        position: relative;
        background: #2a3942;
        border-radius: 24px;
        display: flex;
        align-items: flex-end;
        min-height: 48px;
        transition: all 0.2s ease;
        border: 1.5px solid transparent;
      }

      .wa-compose-input-wrap:focus-within {
        border-color: rgba(0,168,132,0.3);
        background: #313f48;
        box-shadow: 0 0 0 3px rgba(0,168,132,0.08);
      }

      .wa-compose-emoji-btn {
        background: transparent;
        border: none;
        color: #8696a0;
        width: 42px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        flex-shrink: 0;
        font-size: 1.2rem;
        transition: color 0.2s ease;
      }

      .wa-compose-emoji-btn:hover {
        color: #00a884;
      }

      .wa-compose-textarea {
        flex: 1;
        background: transparent;
        border: none;
        color: #e9edef;
        font-size: 16px;
        font-family: inherit;
        padding: 13px 4px 13px 0;
        resize: none;
        outline: none;
        line-height: 1.4;
        max-height: 140px;
        min-height: 22px;
        overflow-y: auto;
        -webkit-appearance: none;
      }

      @media (max-width: 575px) {
        .wa-compose-textarea {
          font-size: 16px !important;
          max-height: 100px;
        }
      }

      .wa-compose-textarea::placeholder {
        color: #8696a0;
      }

      .wa-compose-textarea::-webkit-scrollbar {
        width: 3px;
      }

      .wa-compose-textarea::-webkit-scrollbar-thumb {
        background: rgba(255,255,255,0.1);
        border-radius: 10px;
      }

      .wa-compose-attach-btn {
        background: transparent;
        border: none;
        color: #8696a0;
        width: 42px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        flex-shrink: 0;
        font-size: 1.15rem;
        transition: all 0.2s ease;
      }

      .wa-compose-attach-btn:hover {
        color: #00a884;
        transform: rotate(45deg);
      }

      /* Send Button */
      .wa-send-btn {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: linear-gradient(135deg, #00a884 0%, #008f6f 100%);
        border: none;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        flex-shrink: 0;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 4px 16px rgba(0,168,132,0.3);
        font-size: 1.1rem;
      }

      .wa-send-btn:hover:not(:disabled) {
        transform: scale(1.08);
        box-shadow: 0 6px 20px rgba(0,168,132,0.45);
        background: linear-gradient(135deg, #00c896 0%, #00a884 100%);
      }

      .wa-send-btn:active:not(:disabled) {
        transform: scale(0.92);
        animation: sendPulse 0.3s ease;
      }

      .wa-send-btn:disabled {
        background: #2a3942;
        color: #8696a0;
        cursor: not-allowed;
        box-shadow: none;
        transform: none;
      }

      .wa-send-btn .sending-spinner {
        width: 20px;
        height: 20px;
        border: 2px solid rgba(255,255,255,0.2);
        border-top-color: #fff;
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      /* ── Recipient Picker Modal ── */
      .wa-recipient-overlay {
        position: fixed;
        top: 0; left: 0; right: 0; bottom: 0;
        background: rgba(0,0,0,0.6);
        z-index: 200;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        animation: fadeInScale 0.2s ease;
        backdrop-filter: blur(4px);
      }

      @media (min-width: 768px) {
        .wa-recipient-overlay {
          align-items: center;
        }
      }

      .wa-recipient-modal {
        background: #1f2c34;
        width: 100%;
        max-width: 480px;
        max-height: 70vh;
        border-radius: 20px 20px 0 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        animation: recipientSlide 0.35s cubic-bezier(0.16, 1, 0.3, 1);
        box-shadow: 0 -10px 40px rgba(0,0,0,0.4);
      }

      @media (min-width: 768px) {
        .wa-recipient-modal {
          border-radius: 20px;
          max-height: 60vh;
          box-shadow: 0 20px 60px rgba(0,0,0,0.5);
        }
      }

      .wa-recipient-modal-header {
        padding: 18px 20px 14px;
        border-bottom: 1px solid rgba(255,255,255,0.05);
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .wa-recipient-modal-header h3 {
        margin: 0;
        font-size: 1.05rem;
        font-weight: 700;
        color: #e9edef;
      }

      .wa-recipient-modal-close {
        background: rgba(255,255,255,0.05);
        border: none;
        color: #8696a0;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;
        font-size: 1.1rem;
      }

      .wa-recipient-modal-close:hover {
        background: rgba(255,255,255,0.1);
        color: #e9edef;
      }

      .wa-recipient-search {
        padding: 12px 20px;
      }

      .wa-recipient-search input {
        width: 100%;
        background: #2a3942;
        border: none;
        color: #e9edef;
        padding: 11px 16px;
        border-radius: 10px;
        font-size: 0.9rem;
        font-family: inherit;
        outline: none;
      }

      .wa-recipient-search input:focus {
        box-shadow: 0 0 0 2px rgba(0,168,132,0.3);
      }

      .wa-recipient-search input::placeholder {
        color: #8696a0;
      }

      .wa-recipient-list {
        flex: 1;
        overflow-y: auto;
        padding: 4px 8px 20px;
        
      }

      .wa-recipient-list::-webkit-scrollbar {
        width: 4px;
      }

      .wa-recipient-list::-webkit-scrollbar-thumb {
        background: rgba(255,255,255,0.08);
        border-radius: 10px;
      }

      .wa-recipient-item {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 12px 14px;
        border-radius: 12px;
        cursor: pointer;
        transition: all 0.15s ease;
        border: 1px solid transparent;
      }

      .wa-recipient-item:hover {
        background: rgba(0,168,132,0.08);
      }

      .wa-recipient-item:active {
        transform: scale(0.98);
        background: rgba(0,168,132,0.14);
      }

      .wa-recipient-item.selected {
        background: rgba(0,168,132,0.1);
        border-color: rgba(0,168,132,0.25);
      }

      .wa-recipient-item-avatar {
        width: 42px;
        height: 42px;
        border-radius: 50%;
        background: linear-gradient(135deg, #2a3942, #3b4a54);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #8696a0;
        font-weight: 700;
        font-size: 0.85rem;
        flex-shrink: 0;
      }

      .wa-recipient-item.selected .wa-recipient-item-avatar {
        background: linear-gradient(135deg, #00a884, #025d4b);
        color: #fff;
      }

      .wa-recipient-item-name {
        flex: 1;
        font-size: 0.92rem;
        font-weight: 500;
        color: #e9edef;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .wa-recipient-item-check {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        border: 2px solid #3b4a54;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: all 0.2s ease;
      }

      .wa-recipient-item.selected .wa-recipient-item-check {
        background: #00a884;
        border-color: #00a884;
        color: #fff;
      }

      /* ── Toast ── */
      .wa-toast {
        position: fixed;
        bottom: 100px;
        right: 16px;
        left: 16px;
        z-index: 300;
        animation: toastSlide 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        pointer-events: none;
      }

      @media (min-width: 768px) {
        .wa-toast {
          left: auto;
          width: 360px;
        }
      }

      .wa-toast-inner {
        border-radius: 14px;
        padding: 14px 18px;
        display: flex;
        align-items: center;
        gap: 12px;
        backdrop-filter: blur(20px);
        box-shadow: 0 8px 32px rgba(0,0,0,0.4);
      }

      .wa-toast-inner.success {
        background: linear-gradient(135deg, rgba(0,168,132,0.92), rgba(2,93,75,0.92));
        border: 1px solid rgba(0,210,100,0.2);
      }

      .wa-toast-inner.danger {
        background: linear-gradient(135deg, rgba(220,53,69,0.92), rgba(180,40,55,0.92));
        border: 1px solid rgba(255,100,100,0.2);
      }

      .wa-toast-icon {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: rgba(255,255,255,0.15);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        font-size: 1rem;
      }

      .wa-toast-msg {
        flex: 1;
        color: #fff;
        font-size: 0.88rem;
        font-weight: 600;
        line-height: 1.3;
      }

      /* ── Loading State ── */
      .wa-loading {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 20px;
      }

      .wa-loading-spinner {
        width: 48px;
        height: 48px;
        border: 3px solid rgba(0,168,132,0.15);
        border-top-color: #00a884;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }

      .wa-loading-text {
        color: #8696a0;
        font-size: 0.88rem;
        font-weight: 500;
      }

      /* ── Emoji Quick Bar ── */
      .wa-emoji-bar {
        position: absolute;
        bottom: 100%;
        left: 0;
        right: 0;
        background: #2a3942;
        border-radius: 14px 14px 0 0;
        padding: 10px 14px;
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
        justify-content: center;
        animation: slideInUp 0.2s ease;
        border: 1px solid rgba(255,255,255,0.05);
        border-bottom: none;
        box-shadow: 0 -4px 16px rgba(0,0,0,0.2);
      }

      .wa-emoji-btn {
        width: 38px;
        height: 38px;
        border: none;
        background: rgba(255,255,255,0.04);
        border-radius: 10px;
        font-size: 1.2rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s ease;
      }

      .wa-emoji-btn:hover {
        background: rgba(0,168,132,0.15);
        transform: scale(1.15);
      }

      .wa-emoji-btn:active {
        transform: scale(0.9);
      }

      /* ── Character Counter ── */
      .wa-char-count {
        position: absolute;
        bottom: 4px;
        right: 48px;
        font-size: 0.6rem;
        color: #8696a0;
        opacity: 0;
        transition: opacity 0.2s ease;
      }

      .wa-compose-input-wrap:focus-within .wa-char-count {
        opacity: 1;
      }
    `;

    const style = document.createElement("style");
    style.id = id;
    style.innerHTML = css;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  // ── Fetch logic ──
  const fetchMessages = async (userId) => {
    try {
      setLoading(true);
      const params = userId ? { user_id: userId } : {};
      const res = await axios.get(API_URL1, { params });
      const sorted = res.data ? res.data.sort((a, b) => b.id - a.id) : [];
      setMessages(sorted);
    } catch (err) {
      console.error("Fetch error:", err);
      showToast("Ikibazo mu gushaka ubutumwa.", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const showToast = (msg, type) => {
    setStatus({ show: true, type, msg });
    setTimeout(() => setStatus({ show: false, type: "", msg: "" }), 3200);
  };

  useEffect(() => {
    const fetchRecipients = async () => {
      try {
        const res = await axios.get(API_URL2);
        if (res.data) {
          setRecipients(res.data);
        }
      } catch (err) {
        console.error("Error fetching recipients:", err);
      }
    };
    fetchRecipients();
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!selectedSupId) {
      showToast("Banza uhitemo uwo woherereza.", "danger");
      setShowRecipientPicker(true);
      return;
    }

    if (!messageText.trim()) return;

    setSending(true);

    try {
      await axios.post(API_URL, {
        text_user: messageText,
        sentId: selectedSupId,
      });

      setMessageText("");
      setSelectedSupId("");
      fetchMessages(filterUserId);

      if (navigator.vibrate) navigator.vibrate(50);
      showToast("Ubutumwa bwoherejwe neza! ✓", "success");
    } catch (err) {
      console.error("Save error:", err);
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      showToast("Byanze kohereza ubutumwa.", "danger");
    } finally {
      setSending(false);
    }
  };

  // Keyboard submit (Enter without Shift)
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const quickEmojis = [
    "😊", "👍", "❤️", "🙏", "😂", "🎉", "👏", "🔥",
    "💯", "✅", "⭐", "💪", "😍", "🤝", "👋", "🌟",
  ];

  const insertEmoji = (emoji) => {
    setMessageText((prev) => prev + emoji);
    textareaRef.current?.focus();
  };

  const formatTime = (dateStr) => {
    if (!dateStr) {
      return new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return new Date(dateStr).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="message-page">
      <div className="chat-pattern" />

      {/* ── Header ── */}
      <div className="wa-header">
        <div className="wa-header-inner">
          <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
            <div className="wa-avatar">
              <i className="bi bi-chat-dots-fill" style={{ fontSize: "1.1rem" }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h5 className="wa-header-title">Ibitekerezo n'Ubutumwa</h5>
              <p className="wa-header-sub">
                <span className="online-dot" />
                End-to-end encrypted • {messages.length} messages
              </p>
            </div>
          </div>

          <div className="wa-header-actions">
            <button
              className="wa-header-btn"
              onClick={() => setShowSearch(!showSearch)}
              title="Search"
            >
              <i className="bi bi-search" />
            </button>
            <button
              className="wa-header-btn"
              onClick={() => fetchMessages(filterUserId)}
              disabled={loading}
              title="Refresh"
            >
              <i className={`bi bi-arrow-clockwise ${loading ? "spin" : ""}`} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Search Bar ── */}
      {showSearch && (
        <div className="search-bar">
          <div className="search-bar-inner">
            <div className="search-input-wrap">
              <i className="bi bi-search" />
              <input
                type="number"
                className="search-input"
                placeholder="Filter by User ID..."
                value={filterUserId}
                onChange={(e) => setFilterUserId(e.target.value)}
                autoFocus
              />
            </div>
            <button
              className="search-close-btn"
              onClick={() => {
                fetchMessages(filterUserId);
              }}
            >
              <i className="bi bi-arrow-right" />
            </button>
            <button
              className="search-close-btn"
              onClick={() => {
                setShowSearch(false);
                setFilterUserId("");
                fetchMessages();
              }}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>
        </div>
      )}

      {/* ── Chat Container ── */}
      <div className="wa-chat-container">
        {/* Message List */}
        <div className="wa-message-list" ref={messageListRef}>
          {loading ? (
            <div className="wa-loading">
              <div className="wa-loading-spinner" />
              <p className="wa-loading-text">Ibyo birimo gutegurwa...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="wa-empty-state">
              <div className="wa-empty-icon">
                <i
                  className="bi bi-chat-square-dots"
                  style={{ fontSize: "2.5rem", color: "#00a884" }}
                />
              </div>
              <h5 className="wa-empty-title">Nta butumwa buraboneka</h5>
              <p className="wa-empty-text">
                Andika ubutumwa bwawe hano hepfo maze uohereze!
              </p>
            </div>
          ) : (
            <>
              <div className="wa-date-sep">
                <span>Ubutumwa bwose</span>
              </div>
              {messages.map((m, idx) => {
                const isSent = !!m.sentId;
                return (
                  <div
                    key={m.id}
                    className={`wa-bubble-wrap ${isSent ? "sent" : "received"}`}
                    style={{
                      animationDelay: `${Math.min(idx * 0.04, 0.5)}s`,
                    }}
                  >
                    <div className={`wa-bubble ${isSent ? "sent" : "received"}`}>
                      <div
                        className={`wa-bubble-sender ${
                          isSent ? "sent-label" : "received-label"
                        }`}
                      >
                        <span
                          className={`wa-sender-avatar ${
                            isSent ? "sent-av" : "received-av"
                          }`}
                        >
                          {m.user_id
                            ? m.user_id.toString().charAt(0).toUpperCase()
                            : "?"}
                        </span>
                        {m.user_id ? `User #${m.user_id}` : "Anonymous"}
                      </div>
                      <p className="wa-bubble-text">{m.text_user}</p>
                      <div className="wa-bubble-meta">
                        <span className="wa-bubble-time">
                          {formatTime(m.created_at)}
                        </span>
                        {isSent && (
                          <span className="wa-check">
                            <i className="bi bi-check2-all" />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messageEndRef} />
            </>
          )}
        </div>

        {/* ── Compose Area ── */}
        <div className="wa-compose">
          {/* Recipient Selector */}
          <div className="wa-compose-recipient">
            <button
              className={`wa-recipient-btn ${selectedSupId ? "selected" : ""}`}
              onClick={() => setShowRecipientPicker(true)}
              type="button"
            >
              <span className="rec-icon">
                {selectedSupId ? (
                  selectedRecipientName.charAt(0).toUpperCase()
                ) : (
                  <i className="bi bi-person-plus" />
                )}
              </span>
              <span className="rec-label">
                {selectedSupId
                  ? selectedRecipientName
                  : "Hitamo uwo woherereza..."}
              </span>
              <span className="rec-change">
                {selectedSupId ? "Hindura" : "Hitamo"}
              </span>
            </button>
          </div>

          {/* Input Row */}
          <div className="wa-compose-row">
            <div className="wa-compose-input-wrap" style={{ position: "relative" }}>
              {/* Emoji Quick Bar */}
              {showEmojiHint && (
                <div className="wa-emoji-bar">
                  {quickEmojis.map((emoji, i) => (
                    <button
                      key={i}
                      className="wa-emoji-btn"
                      onClick={() => insertEmoji(emoji)}
                      type="button"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}

              <button
                className="wa-compose-emoji-btn"
                onClick={() => setShowEmojiHint(!showEmojiHint)}
                type="button"
              >
                <i className={`bi ${showEmojiHint ? "bi-x-circle" : "bi-emoji-smile"}`} />
              </button>

              <textarea
                ref={textareaRef}
                className="wa-compose-textarea"
                placeholder="Andika ubutumwa..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setShowEmojiHint(false)}
                rows={1}
              />

              <button className="wa-compose-attach-btn" type="button">
                <i className="bi bi-paperclip" />
              </button>

              {messageText.length > 0 && (
                <span className="wa-char-count">{messageText.length}</span>
              )}
            </div>

            <button
              className="wa-send-btn"
              onClick={handleSubmit}
              disabled={sending || !messageText.trim() || !selectedSupId}
              type="button"
            >
              {sending ? (
                <div className="sending-spinner" />
              ) : (
                <i className="bi bi-send-fill" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── Recipient Picker Modal ── */}
      {showRecipientPicker && (
        <div
          className="wa-recipient-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowRecipientPicker(false);
          }}
        >
          <div className="wa-recipient-modal">
            <div className="wa-recipient-modal-header">
              <h3>
                <i className="bi bi-people-fill me-2" style={{ color: "#00a884" }} />
                Hitamo uwo woherereza
              </h3>
              <button
                className="wa-recipient-modal-close"
                onClick={() => setShowRecipientPicker(false)}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <div className="wa-recipient-search">
              <input
                type="text"
                placeholder="Shakisha izina..."
                value={recipientSearch}
                onChange={(e) => setRecipientSearch(e.target.value)}
                autoFocus
              />
            </div>

            <div className="wa-recipient-list">
              {filteredRecipients.length === 0 ? (
                <div style={{ textAlign: "center", padding: "32px", color: "#8696a0" }}>
                  <i className="bi bi-search" style={{ fontSize: "2rem", display: "block", marginBottom: "12px", opacity: 0.4 }} />
                  <p style={{ margin: 0, fontSize: "0.88rem" }}>Nta muntu ubonetse</p>
                </div>
              ) : (
                filteredRecipients.map((person) => {
                  const name = person.fullname || `Umuyobozi ${person.user_id}`;
                  const isSelected = String(person.user_id) === String(selectedSupId);

                  return (
                    <div
                      key={person.user_id}
                      className={`wa-recipient-item ${isSelected ? "selected" : ""}`}
                      onClick={() => {
                        setSelectedSupId(String(person.user_id));
                        setShowRecipientPicker(false);
                        setRecipientSearch("");
                        textareaRef.current?.focus();
                      }}
                    >
                      <div className="wa-recipient-item-avatar">
                        {name.charAt(0).toUpperCase()}
                      </div>
                      <span className="wa-recipient-item-name">{name}</span>
                      <div className="wa-recipient-item-check">
                        {isSelected && (
                          <i className="bi bi-check2" style={{ fontSize: "0.75rem" }} />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Toast Notification ── */}
      {status.show && (
        <div className="wa-toast">
          <div className={`wa-toast-inner ${status.type}`}>
            <div className="wa-toast-icon">
              <i
                className={`bi ${
                  status.type === "success"
                    ? "bi-check-circle-fill"
                    : "bi-exclamation-triangle-fill"
                }`}
              />
            </div>
            <span className="wa-toast-msg">{status.msg}</span>
          </div>
        </div>
      )}
    </div>
  );
}